// ARM-001 — Admin Agent detail page.
// Profile, owner link, wallet snapshot, recent bookings stamped by agent.

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Divider,
    Paper,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getData, putData, API_ENDPOINTS } from "../../services/apiService.js";
import LoadingSpinner from "../LoadingContext";

const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

const statusChip = (s) => {
    switch (s) {
        case "active":
            return { bg: "#D1FAE5", fg: "#065F46", label: "Active" };
        case "suspended":
            return { bg: "#FEF3C7", fg: "#92400E", label: "Suspended" };
        case "deleted":
            return { bg: "#FEE2E2", fg: "#991B1B", label: "Removed" };
        default:
            return { bg: "#F1F5F9", fg: "#475569", label: s || "—" };
    }
};

function AgentDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [agent, setAgent] = useState(null);
    const [wallet, setWallet] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [fleet, setFleet] = useState({ trucks: [], drivers: [], trips: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [a, w, b, f] = await Promise.all([
                getData(`${API_ENDPOINTS.adminGetAgentById}/${id}`),
                getData(`${API_ENDPOINTS.adminAgentWallet}/${id}`),
                getData(`${API_ENDPOINTS.adminAgentBookings}/${id}`),
                getData(`${API_ENDPOINTS.adminAgentFleet}/${id}`),
            ]);
            if (a?.status === "SUCCESS") {
                setAgent(a.result?.agent || null);
            } else {
                setError(a?.message || "Failed to load agent");
            }
            if (w?.status === "SUCCESS") {
                setWallet(w.result?.wallet || null);
                setTransactions(w.result?.transactions || []);
            }
            if (b?.status === "SUCCESS") setBookings(b.result?.bookings || []);
            if (f?.status === "SUCCESS")
                setFleet({
                    trucks: f.result?.trucks || [],
                    drivers: f.result?.drivers || [],
                    trips: f.result?.trips || [],
                });
        } catch (e) {
            setError("Could not load agent");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const setStatus = async (status) => {
        try {
            const res = await putData(
                `${API_ENDPOINTS.adminSetAgentStatus}/${id}`,
                { status }
            );
            if (res?.status === "SUCCESS") {
                setSuccess(`Agent ${status}`);
                load();
            } else {
                setError(res?.message || "Update failed");
            }
        } catch (e) {
            setError("Update failed");
        }
    };

    if (loading) return <LoadingSpinner />;
    if (!agent)
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Agent not found</Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/app/agents")}
                    sx={{ mt: 2 }}
                >
                    Back to Team
                </Button>
            </Box>
        );

    const fullName = `${agent.first_name || ""} ${agent.last_name || ""}`.trim();
    const ownerName = agent.Owner
        ? `${agent.Owner.first_name || ""} ${agent.Owner.last_name || ""}`.trim()
        : "—";
    const chip = statusChip(agent.status);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/app/agents")}
                    variant="outlined"
                >
                    Back
                </Button>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {agent?.agent_type === "manager" ? "Manager" : "Agent"} details
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => setSuccess(null)}
                >
                    {success}
                </Alert>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                            src={agent.pic || undefined}
                            sx={{ width: 64, height: 64, bgcolor: "#EFF6FF", color: "#3E82F7", fontSize: 20 }}
                        >
                            {getInitials(fullName)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                                {fullName || "—"}
                            </Typography>
                            <Typography sx={{ color: "#6B7280" }}>
                                +{agent.mobile_num}
                            </Typography>
                            {agent.email && (
                                <Typography sx={{ color: "#6B7280", fontSize: 13 }}>
                                    {agent.email}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-end" }}>
                            <Chip
                                label={chip.label}
                                sx={{ bgcolor: chip.bg, color: chip.fg, fontWeight: 700 }}
                            />
                            <Chip
                                label={agent.agent_type === "manager" ? "Manager" : "Agent"}
                                size="small"
                                sx={{
                                    bgcolor: agent.agent_type === "manager" ? "#EDE9FE" : "#E0F2FE",
                                    color: agent.agent_type === "manager" ? "#5B21B6" : "#0369A1",
                                    fontWeight: 600,
                                }}
                            />
                        </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 1.2 }}>
                        <Typography sx={{ color: "#6B7280", fontSize: 13 }}>Owner</Typography>
                        <Typography sx={{ fontSize: 13 }}>{ownerName}</Typography>
                        <Typography sx={{ color: "#6B7280", fontSize: 13 }}>Created</Typography>
                        <Typography sx={{ fontSize: 13 }}>
                            {agent.createdAt
                                ? new Date(agent.createdAt).toLocaleString()
                                : "—"}
                        </Typography>
                        <Typography sx={{ color: "#6B7280", fontSize: 13 }}>
                            Verified
                        </Typography>
                        <Typography sx={{ fontSize: 13 }}>
                            {agent.is_verified ? "Yes" : "No"}
                        </Typography>
                        {agent.aadhar_no && (
                            <>
                                <Typography sx={{ color: "#6B7280", fontSize: 13 }}>
                                    Aadhaar
                                </Typography>
                                <Typography sx={{ fontSize: 13 }}>{agent.aadhar_no}</Typography>
                            </>
                        )}
                        {agent.pan_no && (
                            <>
                                <Typography sx={{ color: "#6B7280", fontSize: 13 }}>
                                    PAN
                                </Typography>
                                <Typography sx={{ fontSize: 13 }}>{agent.pan_no}</Typography>
                            </>
                        )}
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: "flex", gap: 1 }}>
                        {agent.status === "active" ? (
                            <Button variant="outlined" onClick={() => setStatus("suspended")}>
                                Suspend
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={() => setStatus("active")}
                            >
                                Activate
                            </Button>
                        )}
                        <Button
                            color="error"
                            onClick={() => {
                                if (window.confirm("Remove this agent?")) setStatus("deleted");
                            }}
                        >
                            Remove
                        </Button>
                    </Box>
                </Paper>

                <Paper sx={{ p: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1 }}>
                        Wallet
                    </Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#1F2937" }}>
                        ₹{wallet ? Number(wallet.balance || 0).toFixed(2) : "0.00"}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 2 }}>
                        Agent-only wallet (separate from owner wallet).
                    </Typography>
                    <Divider />
                    <Typography sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                        Recent transactions
                    </Typography>
                    {transactions.length === 0 ? (
                        <Typography sx={{ color: "#6B7280", fontSize: 13 }}>
                            No transactions yet.
                        </Typography>
                    ) : (
                        transactions.slice(0, 10).map((t) => (
                            <Box
                                key={t.id}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    py: 0.5,
                                    fontSize: 13,
                                }}
                            >
                                <Typography sx={{ fontSize: 13 }}>
                                    {t.type === "credit" ? "↓" : "↑"} {t.source || t.type}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: t.type === "credit" ? "#059669" : "#DC2626",
                                        fontWeight: 600,
                                    }}
                                >
                                    {t.type === "credit" ? "+" : "-"}₹{Number(t.amount).toFixed(2)}
                                </Typography>
                            </Box>
                        ))
                    )}
                </Paper>
            </Box>

            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1.5 }}>
                    Bookings placed by this agent
                </Typography>
                {bookings.length === 0 ? (
                    <Typography sx={{ color: "#6B7280", fontSize: 13 }}>
                        No bookings stamped yet.
                    </Typography>
                ) : (
                    bookings.slice(0, 50).map((b) => (
                        <Box
                            key={b.package_id}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                py: 0.7,
                                borderBottom: "1px solid #F1F5F9",
                                fontSize: 13,
                            }}
                        >
                            <Typography sx={{ fontSize: 13 }}>
                                #{b.package_id} • {b.package_name || b.package_type || "—"}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                                {b.createdAt
                                    ? new Date(b.createdAt).toLocaleDateString()
                                    : "—"}
                            </Typography>
                        </Box>
                    ))
                )}
            </Paper>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 3, mt: 3 }}>
                <Paper sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                        Trucks added ({fleet.trucks.length})
                    </Typography>
                    {fleet.trucks.slice(0, 10).map((t) => (
                        <Typography key={t.truck_id} sx={{ fontSize: 12, py: 0.3 }}>
                            {t.truck_no} • {t.truck_name || "—"}
                        </Typography>
                    ))}
                </Paper>
                <Paper sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                        Drivers added ({fleet.drivers.length})
                    </Typography>
                    {fleet.drivers.slice(0, 10).map((d) => (
                        <Typography key={d.driver_id} sx={{ fontSize: 12, py: 0.3 }}>
                            {d.first_name} {d.last_name}
                        </Typography>
                    ))}
                </Paper>
                <Paper sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                        Trips created ({fleet.trips.length})
                    </Typography>
                    {fleet.trips.slice(0, 10).map((t) => (
                        <Typography key={t.id} sx={{ fontSize: 12, py: 0.3 }}>
                            #{t.id} •{" "}
                            {t.start_date ? new Date(t.start_date).toLocaleDateString() : "—"}
                        </Typography>
                    ))}
                </Paper>
            </Box>
        </Box>
    );
}

export default AgentDetails;
